'use client';

import Image from 'next/image';
import { LandingPageForm, JourneyType } from '../LandingPageForm/LandingPageForm';

type MarkerStyle = 'numbers' | 'checkmarks';

interface ListItem {
  text: string;
}

interface PartnershipFormPanelProps {
  partnershipLabel?: string;
  partnershipLogo?: string;
  partnershipLogoAlt?: string;

  heading?: string;
  subtitle?: string;

  listItems?: ListItem[];
  markerStyle?: MarkerStyle;

  backgroundImage?: string;
  backgroundAlt?: string;

  regLabel?: string;
  showRegLabel?: boolean;
  showRegToggle?: boolean;
  useCardStyle?: boolean;
  regPlateOnly?: boolean;
  showDurationPicker?: boolean;
  defaultDurationType?: string;
  daysOnly?: boolean;
  useBanner?: boolean;
  bannerCoverType?: string;
  bannerCoverDuration?: string;
  checkboxLabel?: string;
  formIntroText?: string;
  submitButtonText?: string;
  submitButtonColor?: 'orange' | 'purple';
  journey?: JourneyType;
}

export const PARTNERSHIP_FORM_PANEL_COMPONENT_NAME = 'PartnershipFormPanel';

const BADGE_OUTLINE =
  'M13.873 17.0513C13.873 16.2083 14.2079 15.3999 14.8039 14.8039C15.3999 14.2079 16.2083 13.873 17.0513 13.873H18.4959C19.3351 13.8725 20.14 13.5402 20.7351 12.9485L21.7464 11.9372C22.0417 11.6402 22.3929 11.4045 22.7796 11.2436C23.1664 11.0828 23.5811 11 24 11C24.4189 11 24.8336 11.0828 25.2204 11.2436C25.6071 11.4045 25.9583 11.6402 26.2536 11.9372L27.2649 12.9485C27.8601 13.5408 28.6662 13.873 29.5041 13.873H30.9487C31.7917 13.873 32.6001 14.2079 33.1961 14.8039C33.7921 15.3999 34.127 16.2083 34.127 17.0513V18.4959C34.127 19.3338 34.4592 20.1399 35.0515 20.7351L36.0628 21.7464C36.3598 22.0417 36.5955 22.3929 36.7564 22.7796C36.9172 23.1664 37 23.5811 37 24C37 24.4189 36.9172 24.8336 36.7564 25.2204C36.5955 25.6071 36.3598 25.9583 36.0628 26.2536L35.0515 27.2649C34.4598 27.86 34.1275 28.6649 34.127 29.5041V30.9487C34.127 31.7917 33.7921 32.6001 33.1961 33.1961C32.6001 33.7921 31.7917 34.127 30.9487 34.127H29.5041C28.6649 34.1275 27.86 34.4598 27.2649 35.0515L26.2536 36.0628C25.9583 36.3598 25.6071 36.5955 25.2204 36.7564C24.8336 36.9172 24.4189 37 24 37C23.5811 37 23.1664 36.9172 22.7796 36.7564C22.3929 36.5955 22.0417 36.3598 21.7464 36.0628L20.7351 35.0515C20.14 34.4598 19.3351 34.1275 18.4959 34.127H17.0513C16.2083 34.127 15.3999 33.7921 14.8039 33.1961C14.2079 32.6001 13.873 31.7917 13.873 30.9487V29.5041C13.8725 28.6649 13.5402 27.86 12.9485 27.2649L11.9372 26.2536C11.6402 25.9583 11.4045 25.6071 11.2436 25.2204C11.0828 24.8336 11 24.4189 11 24C11 23.5811 11.0828 23.1664 11.2436 22.7796C11.4045 22.3929 11.6402 22.0417 11.9372 21.7464L12.9485 20.7351C13.5402 20.14 13.8725 19.3351 13.873 18.4959V17.0513Z';

const NUMBER_PATHS: Record<number, string> = {
  1: 'M21.0956 21.0963L23.9849 18.207V29.7641',
  2: 'M21.0956 18.207H25.4295C25.8127 18.207 26.1801 18.3592 26.4511 18.6301C26.722 18.901 26.8742 19.2685 26.8742 19.6516V22.5409C26.8742 22.9241 26.722 23.2915 26.4511 23.5624C26.1801 23.8333 25.8127 23.9856 25.4295 23.9856H22.5402C22.1571 23.9856 21.7896 24.1378 21.5187 24.4087C21.2478 24.6796 21.0956 25.0471 21.0956 25.4302V28.3195C21.0956 28.7026 21.2478 29.0701 21.5187 29.341C21.7896 29.6119 22.1571 29.7641 22.5402 29.7641H26.8742',
  3: 'M21.0956 19.6516C21.0956 19.2685 21.2478 18.901 21.5187 18.6301C21.7896 18.3592 22.1571 18.207 22.5402 18.207H25.4295C25.8127 18.207 26.1801 18.3592 26.4511 18.6301C26.722 18.901 26.8742 19.2685 26.8742 19.6516V22.5409C26.8742 22.9241 26.722 23.2915 26.4511 23.5624C26.1801 23.8333 25.8127 23.9856 25.4295 23.9856M25.4295 23.9856H22.5402M25.4295 23.9856C25.8127 23.9856 26.1801 24.1378 26.4511 24.4087C26.722 24.6796 26.8742 25.0471 26.8742 25.4302V28.3195C26.8742 28.7026 26.722 29.0701 26.4511 29.341C26.1801 29.6119 25.8127 29.7641 25.4295 29.7641H22.5402C22.1571 29.7641 21.7896 29.6119 21.5187 29.341C21.2478 29.0701 21.0956 28.7026 21.0956 28.3195',
  4: 'M21.0956 18.207V22.5409C21.0956 22.9241 21.2478 23.2915 21.5187 23.5624C21.7896 23.8333 22.1571 23.9856 22.5402 23.9856H26.8742M26.8742 18.207V29.7641',
  5: 'M21.0956 28.3195C21.0956 28.7026 21.2478 29.0701 21.5187 29.341C21.7896 29.6119 22.1571 29.7641 22.5402 29.7641H25.4295C25.8127 29.7641 26.1801 29.6119 26.4511 29.341C26.722 29.0701 26.8742 28.7026 26.8742 28.3195V25.4302C26.8742 25.0471 26.722 24.6796 26.4511 24.4087C26.1801 24.1378 25.8127 23.9855 25.4295 23.9855H21.0956V18.207H26.8742',
  6: 'M26.8742 19.6516C26.8742 19.2685 26.722 18.901 26.4511 18.6301C26.1801 18.3592 25.8127 18.207 25.4295 18.207H22.5402C22.1571 18.207 21.7896 18.3592 21.5187 18.6301C21.2478 18.901 21.0956 19.2685 21.0956 19.6516V28.3195C21.0956 28.7026 21.2478 29.0701 21.5187 29.341C21.7896 29.6119 22.1571 29.7641 22.5402 29.7641H25.4295C25.8127 29.7641 26.1801 29.6119 26.4511 29.341C26.722 29.0701 26.8742 28.7026 26.8742 28.3195V25.4302C26.8742 25.0471 26.722 24.6796 26.4511 24.4087C26.1801 24.1378 25.8127 23.9855 25.4295 23.9855H21.0956',
};

const NumberBadge = ({ number }: { number: number }) => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="shrink-0" aria-hidden="true">
    <rect width="48" height="48" rx="8" fill="#38363D" />
    <path
      d={`${NUMBER_PATHS[number] || ''} ${BADGE_OUTLINE}`}
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckBadge = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="shrink-0" aria-hidden="true">
    <rect width="48" height="48" rx="8" fill="#38363D" />
    <path
      d={`M17 24L21.5 28.5L31 19 ${BADGE_OUTLINE}`}
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ListMarker = ({ index, style }: { index: number; style: MarkerStyle }) =>
  style === 'numbers' ? <NumberBadge number={index + 1} /> : <CheckBadge />;

const BackgroundImage = ({ backgroundImage, backgroundAlt }: { backgroundImage?: string; backgroundAlt: string }) => {
  if (!backgroundImage) return null;

  return (
    <div className="pointer-events-none absolute inset-0 hidden md:block" aria-hidden="true">
      <Image
        src={backgroundImage}
        alt={backgroundAlt}
        fill
        className="object-cover"
        sizes="(min-width: 768px) 100vw, 0px"
      />
    </div>
  );
};

export const PartnershipFormPanel = ({
  partnershipLabel = 'In partnership with:',
  partnershipLogo,
  partnershipLogoAlt = 'Partner logo',

  heading = 'Get cashback on your policy with TopCashback',
  subtitle = 'How does cashback work?',

  listItems = [],
  markerStyle = 'numbers',

  backgroundImage,
  backgroundAlt = 'Background image',

  regLabel = 'Enter Registration Number',
  showRegLabel = false,
  showRegToggle = false,
  useCardStyle = true,
  regPlateOnly = true,
  showDurationPicker = false,
  defaultDurationType,
  daysOnly,
  useBanner,
  bannerCoverType,
  bannerCoverDuration,
  checkboxLabel,
  formIntroText = 'To get started simply enter your reg...',
  submitButtonText = 'Get a quote',
  submitButtonColor = 'purple',
  journey,
}: PartnershipFormPanelProps) => {
  const displayItems = listItems.slice(0, 6);

  return (
    <section className="bg-background-200 relative w-full overflow-hidden py-8">
      <BackgroundImage backgroundImage={backgroundImage} backgroundAlt={backgroundAlt} />

      <div className="tc-container relative">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between md:gap-[100px]">
          {/* Left column: partnership + form */}
          <div className="flex shrink-0 flex-col gap-4 md:w-[374px]">
            {/* Partnership badge */}
            {(partnershipLabel || partnershipLogo) && (
              <div className="flex items-end gap-1">
                {partnershipLabel && (
                  <span className="text-dark text-[18px] leading-[1.4] font-medium whitespace-nowrap">
                    {partnershipLabel}
                  </span>
                )}
                {partnershipLogo && (
                  <Image
                    src={partnershipLogo}
                    alt={partnershipLogoAlt}
                    width={149}
                    height={44}
                    className="h-[34px] w-auto object-contain md:h-[44px]"
                  />
                )}
              </div>
            )}

            {/* Heading for mobile only, appears above form */}
            {heading && <h2 className="tc-headline mt-0 mb-0 md:hidden">{heading}</h2>}

            <LandingPageForm
              regLabel={regLabel}
              showRegLabel={showRegLabel}
              showRegToggle={showRegToggle}
              useCardStyle={useCardStyle}
              regPlateOnly={regPlateOnly}
              showDurationPicker={showDurationPicker}
              defaultDurationType={defaultDurationType}
              daysOnly={daysOnly}
              useBanner={useBanner}
              bannerCoverType={bannerCoverType}
              bannerCoverDuration={bannerCoverDuration}
              checkboxLabel={checkboxLabel}
              formIntroText={formIntroText}
              submitButtonText={submitButtonText}
              submitButtonColor={submitButtonColor}
              journey={journey || 'none'}
            />
          </div>

          {/* Right column: heading + subtitle + list */}
          <div className="flex flex-col gap-6">
            {/* Heading for desktop only */}
            {heading && <h2 className="tc-headline mt-0 mb-0 hidden md:block">{heading}</h2>}

            {subtitle && (
              <p className="text-dark mt-0 mb-0 text-[18px] leading-[1.2] font-bold md:text-[23px]">{subtitle}</p>
            )}

            {displayItems.length > 0 && (
              <ul className="mt-0 flex list-none flex-col gap-4 pl-0">
                {displayItems.map((item, index) => (
                  <li key={index} className="my-0 flex items-center gap-4">
                    <ListMarker index={index} style={markerStyle} />
                    <span className="text-dark text-[16px] leading-[1.4] font-medium md:text-[18px]">{item.text}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
